import { useCallback, useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { visitsApiRef, Visit } from '@backstage/plugin-home';
import { Entity } from '@backstage/catalog-model';
import {
  KindCounts,
  squadFromOwnership,
  summarizeKindFacets,
} from './viewModel';

type AsyncState<T> = {
  value?: T;
  loading: boolean;
  error?: Error;
  retry: () => void;
};

// Tiny load-once-with-retry state machine (the repo has no react-use).
const useAsyncValue = <T>(load: () => Promise<T>): AsyncState<T> => {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<AsyncState<T>, 'retry'>>({
    loading: true,
  });
  const retry = useCallback(() => {
    setState({ loading: true });
    setAttempt(a => a + 1);
  }, []);
  useEffect(() => {
    let cancelled = false;
    load().then(
      value => !cancelled && setState({ value, loading: false }),
      error => !cancelled && setState({ error, loading: false }),
    );
    return () => {
      cancelled = true;
    };
    // load is intentionally not a dependency: each hook below passes a fresh
    // closure per render, and the fetch must only re-run on retry().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  return { ...state, retry };
};

export type RecentVisit = Pick<
  Visit,
  'id' | 'name' | 'pathname' | 'timestamp' | 'entityRef'
>;

export const useRecentVisits = (limit = 5) => {
  const visitsApi = useApi(visitsApiRef);
  const { value, loading, error, retry } = useAsyncValue<RecentVisit[]>(
    async () => {
      const visits = await visitsApi.list({
        limit: limit + 3,
        orderBy: [{ field: 'timestamp', direction: 'desc' }],
      });
      // The home page itself is the most recent visit — not useful here.
      return visits.filter(v => v.pathname !== '/').slice(0, limit);
    },
  );
  return { visits: value, loading, error, retry };
};

const TECHDOCS_ANNOTATION = 'metadata.annotations.backstage.io/techdocs-ref';

export type HomeCounts = KindCounts & { docs: number };

export const useKindCounts = (): {
  counts?: HomeCounts;
  loading: boolean;
  error?: Error;
} => {
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsyncValue<HomeCounts>(async () => {
    const { facets } = await catalogApi.getEntityFacets({
      facets: ['kind', TECHDOCS_ANNOTATION],
    });
    const docs = (facets[TECHDOCS_ANNOTATION] ?? []).reduce(
      (sum, f) => sum + f.count,
      0,
    );
    return { ...summarizeKindFacets(facets.kind ?? []), docs };
  });
  return { counts: value, loading, error };
};

export type Squad = {
  squadName?: string;
  entities?: Entity[];
};

export const useSquad = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error, retry } = useAsyncValue<Squad>(async () => {
    const identity = await identityApi.getBackstageIdentity();
    const squadName = squadFromOwnership(identity.ownershipEntityRefs);
    if (!squadName) return {};
    const groupRefs = identity.ownershipEntityRefs.filter(ref =>
      ref.startsWith('group:'),
    );
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Component', 'relations.ownedBy': groupRefs },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'spec.type',
        'spec.lifecycle',
      ],
    });
    return { squadName, entities: items.slice(0, 5) };
  });
  return {
    squadName: value?.squadName,
    entities: value?.entities,
    loading,
    error,
    retry,
  };
};
